import { useCards } from "@/pages/Dashboard";
import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Settings, FilePlus } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

function AddNewOption({ label, onAdd }) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");
  if (!adding)
    return (
      <Button
        size="sm"
        variant="ghost"
        className="h-6 text-xs text-blue-600 w-full justify-start"
        onClick={() => setAdding(true)}
        data-testid={`add-new-${label}`}
      >
        <Plus className="w-3 h-3 mr-1" /> Add {label}
      </Button>
    );
  return (
    <div className="flex gap-1 p-1">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-6 text-xs flex-1"
        placeholder={`New ${label}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            onAdd(value.trim());
            setValue("");
            setAdding(false);
          }
        }}
        data-testid={`add-new-${label}-input`}
        autoFocus
      />
      <Button
        size="sm"
        className="h-6 text-xs px-2"
        onClick={() => {
          if (value.trim()) {
            onAdd(value.trim());
            setValue("");
            setAdding(false);
          }
        }}
      >
        Add
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 text-xs px-1"
        onClick={() => {
          setAdding(false);
          setValue("");
        }}
      >
        X
      </Button>
    </div>
  );
}

function DynamicSelect({
  value,
  onChange,
  options,
  configKey,
  label,
  authHeaders,
  onConfigUpdate,
  className,
}) {
  const addOption = async (newVal) => {
    try {
      await axios.post(
        `${API}/plm/config/add-option`,
        { config_key: configKey, value: newVal },
        { headers: authHeaders },
      );
      toast.success(`Added "${newVal}" to ${label}`);
      if (onConfigUpdate) onConfigUpdate();
      onChange(newVal);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className || "h-8 text-sm"}>
        <SelectValue placeholder={`Select ${label}`} />
      </SelectTrigger>
      <SelectContent>
        {options?.map((v) => (
          <SelectItem key={v} value={v}>
            {v}
          </SelectItem>
        ))}
        <div className="border-t">
          <AddNewOption label={label} onAdd={addOption} />
        </div>
      </SelectContent>
    </Select>
  );
}

export default function Configuration() {
  const { cards, config, fetchData, authHeaders } = useCards();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [hwConfigs, setHwConfigs] = useState([]);
  const [configTab, setConfigTab] = useState("cards");
  const [newSkuVertical, setNewSkuVertical] = useState("");
  const [newSkuValue, setNewSkuValue] = useState("");

  const reloadConfig = async () => {
    await fetchData();
  };

  const emptyForm = {
    vertical: config?.verticals?.[0] || "",
    sku: "",
    card_type: config?.card_types?.[0] || "Initial Development",
    card_title: "",
    description: "",
    project_client: "",
    lifecycle_stage: "POC",
    team_owner: "Hardware",
  };

  const openAdd = () => {
    setFormData(emptyForm);
    setHwConfigs([]);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (
      !formData.vertical ||
      !formData.sku ||
      !formData.card_title ||
      !formData.project_client
    ) {
      toast.error("Fill required fields");
      return;
    }
    const validConfigs = hwConfigs.filter((c) => c.name.trim());
    setLoading(true);
    try {
      await axios.post(
        `${API}/plm/cards`,
        { ...formData, hardware_configs: validConfigs },
        { headers: authHeaders },
      );
      toast.success("Card created");
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/plm/cards/${selectedId}`, {
        headers: authHeaders,
      });
      toast.success("Card deleted");
      setShowDeleteModal(false);
      setSelectedId(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const addSkuToVertical = async () => {
    if (!newSkuVertical || !newSkuValue.trim()) {
      toast.error("Select vertical and enter SKU");
      return;
    }
    try {
      await axios.post(
        `${API}/plm/config/add-option`,
        {
          config_key: "sku",
          value: newSkuValue.trim(),
          parent_key: newSkuVertical,
        },
        { headers: authHeaders },
      );
      toast.success(`SKU "${newSkuValue}" added to ${newSkuVertical}`);
      setNewSkuValue("");
      reloadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const skuOptions = useMemo(() => {
    return config?.sku_mapping?.[formData.vertical] || [];
  }, [config, formData.vertical]);

  return (
    <div data-testid="configuration-page">
      <Tabs value={configTab} onValueChange={setConfigTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="cards" data-testid="tab-cards">
            <FilePlus className="w-3 h-3 mr-1" />
            Cards
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="w-3 h-3 mr-1" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* ===== CARDS TAB ===== */}
        <TabsContent value="cards">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm text-slate-900">
              All Cards ({cards.length})
            </h3>
            <Button size="sm" onClick={openAdd} data-testid="new-card-btn">
              <Plus className="w-4 h-4 mr-1" /> New Card
            </Button>
          </div>
          <div className="border rounded overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Id</TableHead>
                  {/* <TableHead>SKU</TableHead> */}
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Blockers</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cards.map((card) => (
                  <TableRow key={card.id} data-testid={`config-row-${card.id}`}>
                    <TableCell className="text-xs">{card.id}</TableCell>
                    {/* <TableCell className="text-xs font-mono font-bold">
                      {card.sku}
                    </TableCell> */}
                    <TableCell className="text-sm max-w-[200px] truncate">
                      {card.title}
                    </TableCell>
                    <TableCell className="text-xs">
                      {card.description}
                    </TableCell>
                    <TableCell className="text-xs">{card.priority}</TableCell>
                    <TableCell className="text-xs">{card.assignee}</TableCell>
                    <TableCell>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${card.overall_status === "Completed" ? "bg-emerald-100 text-emerald-700" : card.overall_status === "Blocked" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
                      >
                        {card.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {card.blocker_count || 0}
                    </TableCell>
                    <TableCell className="text-xs">
                      {card.createdAt
                        ? new Date(card.createdAt).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500"
                        onClick={() => {
                          setSelectedId(card.id);
                          setShowDeleteModal(true);
                        }}
                        data-testid={`delete-${card.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== SETTINGS TAB ===== */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Verticals */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Verticals
              </h4>
              <div className="space-y-1 mb-2">
                {config?.verticals?.map((v) => (
                  <div
                    key={v}
                    className="text-xs bg-slate-50 px-2 py-1 rounded"
                  >
                    {v}
                  </div>
                ))}
              </div>
              <AddNewOption
                label="Vertical"
                onAdd={async (val) => {
                  try {
                    await axios.post(
                      `${API}/plm/config/add-option`,
                      { config_key: "verticals", value: val },
                      { headers: authHeaders },
                    );
                    toast.success(`Added "${val}"`);
                    reloadConfig();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed");
                  }
                }}
              />
            </div>

            {/* SKU Mapping */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                SKU Mapping
              </h4>
              <div className="space-y-1 mb-2 max-h-[200px] overflow-y-auto">
                {config?.verticals?.map((v) => (
                  <div key={v} className="text-xs">
                    <span className="font-medium text-slate-700">{v}:</span>
                    <span className="text-slate-500 ml-1">
                      {(config?.sku_mapping?.[v] || []).join(", ") || "none"}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-1 items-center">
                <Select
                  value={newSkuVertical}
                  onValueChange={setNewSkuVertical}
                >
                  <SelectTrigger className="h-6 text-xs w-[100px]">
                    <SelectValue placeholder="Vertical" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.verticals?.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newSkuValue}
                  onChange={(e) => setNewSkuValue(e.target.value)}
                  placeholder="SKU name"
                  className="h-6 text-xs flex-1"
                  onKeyDown={(e) => e.key === "Enter" && addSkuToVertical()}
                />
                <Button
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={addSkuToVertical}
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Card Types */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Card Types
              </h4>
              <div className="space-y-1 mb-2">
                {config?.card_types?.map((v) => (
                  <div
                    key={v}
                    className="text-xs bg-slate-50 px-2 py-1 rounded"
                  >
                    {v}
                  </div>
                ))}
              </div>
              <AddNewOption
                label="Card Type"
                onAdd={async (val) => {
                  try {
                    await axios.post(
                      `${API}/plm/config/add-option`,
                      { config_key: "card_types", value: val },
                      { headers: authHeaders },
                    );
                    toast.success(`Added "${val}"`);
                    reloadConfig();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed");
                  }
                }}
              />
            </div>

            {/* Teams */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Teams
              </h4>
              <div className="space-y-1 mb-2">
                {config?.teams?.map((v) => (
                  <div
                    key={v}
                    className="text-xs bg-slate-50 px-2 py-1 rounded"
                  >
                    {v}
                  </div>
                ))}
              </div>
              <AddNewOption
                label="Team"
                onAdd={async (val) => {
                  try {
                    await axios.post(
                      `${API}/plm/config/add-option`,
                      { config_key: "teams", value: val },
                      { headers: authHeaders },
                    );
                    toast.success(`Added "${val}"`);
                    reloadConfig();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed");
                  }
                }}
              />
            </div>

            {/* Lifecycle Stages */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Lifecycle Stages
              </h4>
              <div className="space-y-1 mb-2">
                {config?.lifecycle_stages?.map((v) => (
                  <div
                    key={v}
                    className="text-xs bg-slate-50 px-2 py-1 rounded"
                  >
                    {v}
                  </div>
                ))}
              </div>
              <AddNewOption
                label="Stage"
                onAdd={async (val) => {
                  try {
                    await axios.post(
                      `${API}/plm/config/add-option`,
                      { config_key: "lifecycle_stages", value: val },
                      { headers: authHeaders },
                    );
                    toast.success(`Added "${val}"`);
                    reloadConfig();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed");
                  }
                }}
              />
            </div>

            {/* Ownership */}
            <div className="border rounded p-3">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Ownership Types
              </h4>
              <div className="space-y-1 mb-2">
                {config?.ownership_options?.map((v) => (
                  <div
                    key={v}
                    className="text-xs bg-slate-50 px-2 py-1 rounded"
                  >
                    {v}
                  </div>
                ))}
              </div>
              <AddNewOption
                label="Ownership"
                onAdd={async (val) => {
                  try {
                    await axios.post(
                      `${API}/plm/config/add-option`,
                      { config_key: "ownership_options", value: val },
                      { headers: authHeaders },
                    );
                    toast.success(`Added "${val}"`);
                    reloadConfig();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed");
                  }
                }}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== CREATE CARD MODAL ===== */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Card</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Vertical *</Label>
              <DynamicSelect
                value={formData.vertical}
                onChange={(v) =>
                  setFormData((p) => ({ ...p, vertical: v, sku: "" }))
                }
                options={config?.verticals}
                configKey="verticals"
                label="Vertical"
                authHeaders={authHeaders}
                onConfigUpdate={reloadConfig}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">SKU *</Label>
              <DynamicSelect
                value={formData.sku}
                onChange={(v) => setFormData((p) => ({ ...p, sku: v }))}
                options={skuOptions}
                configKey="sku"
                label="SKU"
                authHeaders={authHeaders}
                onConfigUpdate={reloadConfig}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Card Type *</Label>
              <DynamicSelect
                value={formData.card_type}
                onChange={(v) => setFormData((p) => ({ ...p, card_type: v }))}
                options={config?.card_types}
                configKey="card_types"
                label="Card Type"
                authHeaders={authHeaders}
                onConfigUpdate={reloadConfig}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Lifecycle Stage</Label>
              <DynamicSelect
                value={formData.lifecycle_stage}
                onChange={(v) =>
                  setFormData((p) => ({ ...p, lifecycle_stage: v }))
                }
                options={config?.lifecycle_stages}
                configKey="lifecycle_stages"
                label="Stage"
                authHeaders={authHeaders}
                onConfigUpdate={reloadConfig}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Team Owner</Label>
              <DynamicSelect
                value={formData.team_owner}
                onChange={(v) => setFormData((p) => ({ ...p, team_owner: v }))}
                options={config?.teams}
                configKey="teams"
                label="Team"
                authHeaders={authHeaders}
                onConfigUpdate={reloadConfig}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Client / Project *</Label>
              <Input
                value={formData.project_client || ""}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, project_client: e.target.value }))
                }
                className="h-8 text-sm"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Card Title *</Label>
              <Input
                value={formData.card_title || ""}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, card_title: e.target.value }))
                }
                className="h-8 text-sm"
                data-testid="card-title-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, description: e.target.value }))
                }
                className="text-sm min-h-[60px]"
                placeholder="Project details, scope, goals..."
                data-testid="card-description-input"
              />
            </div>
            {/* Multi-HW Config */}
            <div className="col-span-2 border-t pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium">
                  Multi-Hardware/Firmware Config (Optional)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() =>
                    setHwConfigs((p) => [...p, { name: "", accessory: "" }])
                  }
                  data-testid="add-hw-config-btn"
                >
                  + Add Config
                </Button>
              </div>
              {hwConfigs.map((cfg, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-2">
                  <Input
                    value={cfg.name}
                    onChange={(e) =>
                      setHwConfigs((p) =>
                        p.map((c, i) =>
                          i === idx ? { ...c, name: e.target.value } : c,
                        ),
                      )
                    }
                    placeholder="Name (e.g. Node-C1)"
                    className="flex-1 h-7 text-xs"
                    data-testid={`hw-config-name-${idx}`}
                  />
                  <Input
                    value={cfg.accessory}
                    onChange={(e) =>
                      setHwConfigs((p) =>
                        p.map((c, i) =>
                          i === idx ? { ...c, accessory: e.target.value } : c,
                        ),
                      )
                    }
                    placeholder="Accessory (e.g. Belts)"
                    className="flex-1 h-7 text-xs"
                    data-testid={`hw-config-acc-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 text-red-500 p-0"
                    onClick={() =>
                      setHwConfigs((p) => p.filter((_, i) => i !== idx))
                    }
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {hwConfigs.length > 0 && (
                <p className="text-[10px] text-slate-400">
                  Each config creates separate HW + FW tracks
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              data-testid="save-card-btn"
            >
              {loading ? "Creating..." : "Create Card"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Card</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will permanently delete this card and all its data.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={loading}
              data-testid="confirm-delete-btn"
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { DynamicSelect };
